/**
 * Error Tracking Service
 * 
 * Provides integration with error tracking services:
 * - Sentry (primary): Detailed error tracking and analytics
 * - Firebase Crashlytics (backup): Free, unlimited crash reporting
 * 
 * Crashlytics automatically takes over if Sentry is not configured or fails.
 */

import Constants from 'expo-constants';
import { NativeModules, Platform } from 'react-native';
import { logger } from './logger';

/** Expo Go does not ship @sentry/react-native native code; dynamic import + init often hangs or crashes. */
function isExpoGo(): boolean {
  try {
    return (
      Constants.appOwnership === 'expo' ||
      Constants.executionEnvironment === 'storeClient'
    );
  } catch {
    return false;
  }
}

/** True only when a dev client / release build has @react-native-firebase/app linked (never in Expo Go). */
function isReactNativeFirebaseNativeLinked(): boolean {
  if (Platform.OS === 'web') return false;
  return (NativeModules as Record<string, unknown>).RNFBAppModule != null;
}

let sentryInitialized = false;
let Sentry: any = null;
let crashlyticsInitialized = false;
let Crashlytics: any = null;

/** DSN from env (inlined at bundle time) or from app.config `extra.sentryDsn` (manifest / OTA). */
export function getSentryDsn(): string | undefined {
  const fromEnv = process.env.EXPO_PUBLIC_SENTRY_DSN;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return fromEnv;
  }
  try {
    const extra = Constants.expoConfig?.extra as Record<string, unknown> | undefined;
    const fromExtra = extra?.sentryDsn;
    if (typeof fromExtra === 'string' && fromExtra.length > 0) {
      return fromExtra;
    }
  } catch {
    // ignore
  }
  return undefined;
}

/**
 * Initialize Firebase Crashlytics (backup error tracking)
 * Works on iOS/Android native builds (not Expo Go)
 */
async function initCrashlytics(): Promise<void> {
  if (crashlyticsInitialized || Platform.OS === 'web') {
    return;
  }

  if (!isReactNativeFirebaseNativeLinked()) {
    if (__DEV__) {
      logger.debug(
        'Crashlytics skipped: RNFB native module not linked (use a dev build with @react-native-firebase/app, not Expo Go)',
      );
    }
    return;
  }

  try {
    // Try to load Firebase Crashlytics (native must be linked or import() throws)
    const crashlyticsModule = await import('@react-native-firebase/crashlytics');
    Crashlytics = crashlyticsModule.default;

    if (Crashlytics) {
      // Enable crash collection
      await Crashlytics().setCrashlyticsCollectionEnabled(true);
      
      // Set app version for tracking
      try {
        const Constants = require('expo-constants').default;
        const version = Constants.expoConfig?.version || Constants.manifest?.version || '1.0.0';
        const buildNumber = Constants.expoConfig?.ios?.buildNumber || Constants.expoConfig?.android?.versionCode || '1';
        await Crashlytics().setAttribute('version', version);
        await Crashlytics().setAttribute('buildNumber', buildNumber);
      } catch (error) {
        // Ignore version setting errors
      }

      crashlyticsInitialized = true;
      logger.info('Crashlytics initialized as backup', { platform: Platform.OS });
    }
  } catch (error) {
    // Crashlytics not available (e.g., in Expo Go or not installed)
    // This is fine - it's a backup service
    if (__DEV__) {
      logger.debug('Crashlytics not available (expected in Expo Go)', error as Error);
    }
  }
}

/**
 * Initialize error tracking with Sentry (primary) and Crashlytics (backup)
 * Call this early in the app lifecycle (e.g., in App.tsx or _layout.tsx)
 */
export async function initErrorTracking(): Promise<void> {
  // Initialize Crashlytics as backup in background so startup does not block on native bridge setup.
  void initCrashlytics();

  if (sentryInitialized) {
    return;
  }

  const dsn = getSentryDsn();
  if (!dsn) {
    logger.info(
      'Sentry not configured (no EXPO_PUBLIC_SENTRY_DSN or extra.sentryDsn in manifest). Crashlytics-only if linked.',
    );
    return;
  }

  // @sentry/react-native targets native runtimes; initializing it in mobile Safari (Expo web) has caused
  // renderer instability. Use Safari Web Inspector for web dev logs; add @sentry/browser if you need web DSN.
  if (Platform.OS === 'web') {
    if (__DEV__) {
      logger.info('Sentry skipped on web (use native dev client or Safari console for this project).');
    }
    return;
  }

  if (isExpoGo()) {
    console.warn(
      '[errorTracking] Sentry skipped in Expo Go (no native Sentry). Use a development build (`s` in Expo CLI → dev client) or unset EXPO_PUBLIC_SENTRY_DSN.',
    );
    return;
  }

  try {
    // Dynamically import Sentry to avoid bundling if not installed
    const SentryModule = await import('@sentry/react-native');
    Sentry = SentryModule.default;

    if (!Sentry) {
      logger.warn('Sentry module loaded but default export is null');
      return;
    }

    // Get app version for release tracking
    let release: string | undefined;
    try {
      const Constants = require('expo-constants').default;
      const version = Constants.expoConfig?.version || Constants.manifest?.version || '1.0.0';
      const buildNumber = Constants.expoConfig?.ios?.buildNumber || Constants.expoConfig?.android?.versionCode || '1';
      const platform = Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'web';
      release = `${platform}@${version}+${buildNumber}`;
    } catch (error) {
      logger.warn(
        'Failed to get app version for Sentry release',
        undefined,
        error instanceof Error ? error : new Error(String(error)),
      );
    }

    const appEnv = Constants.expoConfig?.extra?.appEnv as string | undefined;
    const sentryEnvironment = __DEV__
      ? 'development'
      : appEnv === 'staging'
        ? 'staging'
        : 'production';

    // Initialize Sentry
    Sentry.init({
      dsn,
      // Staging/preview builds are often run on dev clients; still send so Issues populate.
      enableInExpoDevelopment: appEnv === 'staging' || __DEV__,
      debug: __DEV__,
      environment: sentryEnvironment,
      release, // Track errors by app version
      tracesSampleRate: __DEV__ ? 1.0 : 0.1, // 100% in dev, 10% in prod
      // Explicitly off: native profiling can stress the bridge; avoids extra Swift/runtime sync paths.
      profilesSampleRate: 0,
      beforeSend(event: any, hint: any) {
        // Filter out Firestore internal assertion failures
        if (event.exception) {
          const errorMessage = event.exception.values?.[0]?.value || '';
          if (errorMessage.includes('FIRESTORE') && errorMessage.includes('INTERNAL ASSERTION FAILED')) {
            return null; // Don't send to Sentry
          }
        }
        return event;
      },
    });

    // Set Sentry in logger
    logger.setErrorTrackingService(Sentry);

    sentryInitialized = true;
    logger.info('Sentry initialized as primary error tracking', { platform: Platform.OS });
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    const backup = crashlyticsInitialized ? 'Crashlytics is available as backup.' : 'Crashlytics is not linked; only local logs apply.';
    console.warn('[errorTracking] Sentry failed:', err.message);
    logger.warn(
      `Failed to initialize Sentry. ${backup} Install with: npx expo install @sentry/react-native (then rebuild dev client). Or unset EXPO_PUBLIC_SENTRY_DSN to skip.`,
      { sentryError: err.message },
      err,
    );
  }
}

/** Log Sentry manifest hints for staging QA (call after initErrorTracking settles — e.g. from RootLayout `.finally()`). */
export function logStagingSentryDiagnosticsToConsole(): void {
  try {
    const appEnv = Constants.expoConfig?.extra?.appEnv as string | undefined;
    if (Platform.OS !== 'web' && appEnv === 'staging' && !__DEV__) {
      const extra = Constants.expoConfig?.extra as { sentryDsn?: string } | undefined;
      console.warn(
        `[Tripvio staging] Sentry DSN resolved=${Boolean(getSentryDsn())} manifest.extra.sentryDsn=${Boolean(extra?.sentryDsn)} — open More→Profile: tripvio://nav/more-profile`,
      );
    }
  } catch {
    // ignore
  }
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error, context?: Record<string, any>): void {
  if (Sentry) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else {
    logger.error('Exception captured but Sentry not initialized', error, context);
  }
}

/**
 * Capture a message
 * Tries Sentry first, falls back to Crashlytics if Sentry is not available
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info', context?: Record<string, any>): void {
  let sentToService = false;

  // Try Sentry first (primary)
  if (Sentry) {
    try {
      Sentry.captureMessage(message, {
        level,
        extra: context,
      });
      sentToService = true;
    } catch (sentryError) {
      logger.warn('Failed to send message to Sentry, trying Crashlytics', sentryError as Error);
    }
  }

  // Fall back to Crashlytics for error-level messages
  if (Crashlytics && level === 'error') {
    try {
      // Set custom keys for context
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
          Crashlytics().setAttribute(key, stringValue);
        });
      }
      
      // Log error message to Crashlytics
      Crashlytics().log(message);
      if (!sentToService) {
        logger.info('Error message sent to Crashlytics (backup)', { message });
      }
    } catch (crashlyticsError) {
      if (level === 'error') {
        logger.error(message, undefined, context);
      } else if (level === 'warning') {
        logger.warn(message, context);
      } else {
        logger.info(message, context);
      }
    }
  } else if (!sentToService) {
    if (level === 'error') {
      logger.error(message, undefined, context);
    } else if (level === 'warning') {
      logger.warn(message, context);
    } else {
      logger.info(message, context);
    }
  }
}

/**
 * Set user context for error tracking
 */
export function setUserContext(userId: string, email?: string, username?: string): void {
  if (Sentry) {
    Sentry.setUser({
      id: userId,
      email,
      username,
    });
  }

  if (Crashlytics) {
    try {
      Crashlytics().setUserId(userId);
    } catch {
      // Ignore Crashlytics errors
    }
  }
}

/**
 * Clear user context
 */
export function clearUserContext(): void {
  // Clear in Sentry
  if (Sentry) {
    Sentry.setUser(null);
  }

  // Clear in Crashlytics
  if (Crashlytics) {
    try {
      Crashlytics().setUserId('');
    } catch (error) {
      // Ignore Crashlytics errors
    }
  }
}

/**
 * Record a non-fatal error to Firebase Crashlytics (shows as an issue in the console).
 *
 * Breadcrumbs and {@link captureMessage} with Crashlytics only call `log()`, which does **not** open an
 * issue until a native crash occurs. Use this when you need visibility for JS-side failures, freezes
 * you recover from, or watchdogs (e.g. trips list stuck loading).
 */
export async function recordCrashlyticsNonFatal(
  error: Error,
  context?: Record<string, unknown>,
): Promise<void> {
  if (Platform.OS === 'web' || !isReactNativeFirebaseNativeLinked()) {
    return;
  }
  try {
    const crashlyticsModule = await import('@react-native-firebase/crashlytics');
    const Cr = crashlyticsModule.default;
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        const stringValue =
          value === undefined || value === null
            ? ''
            : typeof value === 'string'
              ? value
              : JSON.stringify(value);
        const attrKey = `nf_${key}`.slice(0, 32);
        await Cr().setAttribute(attrKey, stringValue.slice(0, 200));
      }
    }
    Cr().recordError(error);
  } catch {
    // ignore
  }
}

/**
 * Add breadcrumb for debugging (Sentry + Firebase Crashlytics log when linked).
 * For native iOS crashes: symbolicate in Xcode Organizer with the dSYM from the same EAS/build archive.
 */
export function addBreadcrumb(message: string, category: string, level: 'debug' | 'info' | 'warning' | 'error' = 'info', data?: Record<string, any>): void {
  if (Sentry) {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
      timestamp: Date.now() / 1000,
    });
  }

  if (Crashlytics) {
    try {
      const suffix = data ? ` ${JSON.stringify(data)}` : '';
      const line = `[${category}] ${message}${suffix}`.slice(0, 1200);
      Crashlytics().log(line);
    } catch {
      // ignore
    }
  }
}

/**
 * Set additional context
 */
export function setContext(key: string, context: Record<string, any>): void {
  if (Sentry) {
    Sentry.setContext(key, context);
  }
}

export { Sentry, Crashlytics };

